//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import BulkActionSelect from '@components/atoms/bulk-action-select/BulkActionSelect';
import { OverlayIconButton } from '@components/atoms/button/Button';
import { MediaAtomPositionMixins } from '@components/atoms/media/MediaAtomPositionMixins';
import { ContentCardRangeSelectContext } from '@components/organisms/content-card-grid/ContentCardRangeSelectContext';
import {
  useGetIsAnyItemBulkSelected,
  useGetIsBulkSelected,
} from '@hooks/useGetIsBulkSelected';
import ItemId from '@models/ItemId';
import PartialCuid from '@models/PartialCuid';
import { UniversalImageActionsView } from '@models/UniversalImageActionsView';
import { UniversalItemType } from '@models/UniversalItemType';
import { getBulkActionsActions } from '@react-bridge/wrappers/ng-service-provider';
import { IMediaTrackableComponent } from '@tracker/mediaTracking/mediaTrackingModel';
import { openUia } from '@utils/uia';
import { ITrackableComponent, withTracking } from '@utils/withTracking';

// Accepts a actionLayer prop so that this component can be responsible
// for showing / hiding the dev-provided image overlay. If we conditionally rendered
// that prop at the Media atom level instead, then the logic specific to this
// component must leak up into that component. This allows us to turn bulk selection
// on and off with a single switch in Media.
interface Props
  extends React.HTMLAttributes<HTMLDivElement>,
    ITrackableComponent,
    IMediaTrackableComponent {
  actionLayer?: (isMediaHovered: boolean) => ReactNode;
  isMediaHovered: boolean;
  itemType: UniversalItemType;
  id: ItemId;
}

/**
 * The BulkSelectionLayer atom is used exclusively in the Media atom to support
 * the bulk selection workflow. This component is built to render the Media atom's
 * imageOverlay prop with a BulkActionSelect atom injected in the top left of the
 * image. Once the user has bulk selected at least one item, the developer-provided
 * overlay is hidden and we cover the image with a bulk selection workflow overlay instead,
 * which allows the user to click anywhere to select an item.
 */
const BulkSelectionLayer = ({
  actionLayer,
  id,
  isMediaHovered,
  itemType,
  mediaTrackingConfig,
  trackingId,
}: Props) => {
  const dispatch = useDispatch();

  // We want to track hover events on the bulk action wrapper so that we can show the zoom
  // icon in the top right. This needs to be tracked separately from the container hover
  // events because the zoom icon is asibling on the container, whereas it is a child of
  // the wrapper.
  const [isBulkActionWrapperHovered, setIsBulkActionWrapperHovered] =
    useState(false);
  const [isBulkActionContainerHovered, setIsBulkActionContainerHovered] =
    useState(false);
  const { lastSelectedItem, orderedPartialCuids, setLastSelectedItem } =
    useContext(ContentCardRangeSelectContext);
  const isAnyItemSelected = useGetIsAnyItemBulkSelected();
  const isItemSelected = useGetIsBulkSelected(itemType, id);

  // Disable the native shift+click behavior of highlighting/selecting the items within a range
  // while bulk selecting items. Instead we want to bulk select our items but not have have them
  // highlighted/native select behavior
  const blockNativeShiftSelect = (e: KeyboardEvent | React.MouseEvent) => {
    if (isBulkActionWrapperHovered) {
      document.onselectstart = () => !e.shiftKey;
    }
  };

  const onUiaButtonClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    openUia(
      { itemId: id, itemType },
      UniversalImageActionsView.Details.name,
      () => null,
      event,
    );
  };

  const onBulkSelectCoverClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const cuid = PartialCuid.build({ itemType, id });
    const currentItemIndex = orderedPartialCuids.indexOf(cuid);
    const isShiftPressed = event.shiftKey;
    if (isItemSelected) {
      dispatch(
        getBulkActionsActions().unselectItems({
          currentItemIndex,
          orderedPartialCuids,
          lastSelectedItem,
          cuid,
          isShiftPressed,
          setLastSelectedItem,
        }),
      );
    } else {
      dispatch(
        getBulkActionsActions().selectItems({
          currentItemIndex,
          orderedPartialCuids,
          lastSelectedItem,
          cuid,
          isShiftPressed,
          setLastSelectedItem,
          source: mediaTrackingConfig?.source,
        }),
      );
    }
  };

  const onWrapperMouseEnter = () => setIsBulkActionWrapperHovered(true);

  const onWrapperMouseLeave = () => setIsBulkActionWrapperHovered(false);

  useEffect(() => {
    window.addEventListener('keyup', blockNativeShiftSelect);
    window.addEventListener('keydown', blockNativeShiftSelect);

    return () => {
      window.removeEventListener('keyup', blockNativeShiftSelect);
      window.removeEventListener('keydown', blockNativeShiftSelect);

      // Clean up hover state if it is still hovered
      setIsBulkActionContainerHovered(false);
    };
  });

  const onContainerMouseEnter = () => {
    setIsBulkActionContainerHovered(true);
  };

  const onContainerMouseLeave = () => {
    setIsBulkActionContainerHovered(false);
  };

  const showBulkActionSelectButton = isMediaHovered && !isAnyItemSelected;
  const showBulkActionOverlayCover = isAnyItemSelected;

  return (
    <StyledBulkActionSelectCoverWrapper
      onMouseEnter={onWrapperMouseEnter}
      onMouseLeave={onWrapperMouseLeave}
    >
      {!isAnyItemSelected && actionLayer?.(isBulkActionWrapperHovered)}
      {showBulkActionSelectButton && (
        <StyledBulkActionSelect
          id={id}
          itemType={itemType}
          mediaTrackingConfig={mediaTrackingConfig}
          shouldShowHoverState={isAnyItemSelected && isMediaHovered}
          trackingId={trackingId}
        />
      )}
      {showBulkActionOverlayCover && (
        <>
          <StyledBulkActionSelectCoverContainer
            onClick={onBulkSelectCoverClick}
            onMouseEnter={onContainerMouseEnter}
            onMouseLeave={onContainerMouseLeave}
            trackingId={trackingId}
          >
            <StyledBulkActionSelect
              id={id}
              itemType={itemType}
              mediaTrackingConfig={mediaTrackingConfig}
              shouldShowHoverState={isBulkActionContainerHovered}
              trackingId={trackingId}
            />
          </StyledBulkActionSelectCoverContainer>
          {isBulkActionWrapperHovered && (
            <StyledOverlayIconButton
              iconName='zoom'
              onClick={onUiaButtonClick}
            />
          )}
        </>
      )}
      <BulkSelectionBorder isItemSelected={isItemSelected} />
    </StyledBulkActionSelectCoverWrapper>
  );
};

const BulkSelectionBorder = styled.div<{ isItemSelected: boolean }>(
  ({ isItemSelected, theme }) => `
  ${MediaAtomPositionMixins.Cover};
  border: solid ${theme.spacings[1]} ${
    isItemSelected ? theme.colors.aliases.primary : 'transparent'
  };
  pointer-events: none;
`,
);

const StyledBulkActionSelectCoverWrapper = withTracking(styled.div`
  ${MediaAtomPositionMixins.Cover};
`);

const StyledBulkActionSelectCoverContainer = withTracking(styled.div`
  ${MediaAtomPositionMixins.Cover};
`);

// This needs a z-index of 100 to appear over the copyright icon from MediaTypeOverlayIcon
const StyledOverlayIconButton = styled(OverlayIconButton)`
  ${MediaAtomPositionMixins.TopRight};
  z-index: 100;
`;

const StyledBulkActionSelect = styled(BulkActionSelect)`
  ${MediaAtomPositionMixins.TopLeft};
`;

export default BulkSelectionLayer;
